import { Modal } from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import Styles from "./UploadLDC.module.css";
import { Close, EmailOutlined } from "@mui/icons-material";
import { ErrorMessage } from "../../../../commons/Toast/ErrorMessage";
import { DndContext } from "@dnd-kit/core";
import { useDroppable } from "@dnd-kit/react";
import {
  getWalletAccountsSpecial,
  walletAccountApi,
} from "../../../../Services/Payments/PaymentApi";
import { useDispatch, useSelector } from "react-redux";
import SelectInput from "../../../../commons/Inputs/Select/SelectInput";
import * as pdfjsLib from "pdfjs-dist";
import {
  updateContractData,
  uploadLDC,
} from "../../../../Services/BuySell/Listing";
import {
  deliveryFinalSchedule,
  handleCreateSchedule,
  sendErrorMail,
  userFinalSettlement,
} from "../../../../Services/Schedule/schedule";
import {
  fetchActiveConnection,
  fetchMainScreenActiveConnection,
} from "../../../../Redux/Slices/CapacitySlice";
import { useLocation } from "react-router-dom";
import ConfirmationModal from "../../../../commons/ConfirmationModal/ConfirmationModal";
import axios from "axios";
import ChargeReqModal from "./ChargeReqModal";

// ── Constants ───────────────────────────────────────────────────────────────
const bearerList = [
  { label: "Buyer", value: "BUYER" },
  { label: "Seller", value: "SELLER" },
  { label: "Applicant (GNA)", value: "APPLICANT" },
];

const payeeList = [
  { label: "Buyer", value: "BUYER" },
  { label: "Seller", value: "SELLER" },
  { label: "Applicant (GNA)", value: "APPLICANT" },
];

if (typeof window !== "undefined" && "Worker" in window) {
  pdfjsLib.GlobalWorkerOptions.workerPort = new Worker(
    new URL("pdfjs-dist/build/pdf.worker.mjs", import.meta.url),
    { type: "module" },
  );
}

// ── Component ────────────────────────────────────────────────────────────────
const UploadLDC = ({
  open,
  onClose,
  connectionId,
  mode,
  capacityId,
  connection,
  clickedIndex,
}) => {
  const dispatch = useDispatch();
  const inputRef = useRef();
  const location = useLocation();

  // ── Selectors ──────────────────────────────────────────────────────────────
  const id = useSelector((state) => state.CapacitySlice?.activeMain?.id);
  const token = useSelector((state) => state?.persistedReducer?.token);

  const { setNodeRef, isOver } = useDroppable({ id: "ldc-dropzone" });
  const userData = useSelector((state) => state?.persistedReducer?.user);
  const tenderData = useSelector(
    (state) => state?.auctionSlice?.tenderSpecefic,
  );

  const activeRole = userData?.activeRole;
  const activeId =
    activeRole === "CONSUMER"
      ? userData?.activeConsumerId
      : userData?.activeGeneratorId;
  const activeEntity =
    activeRole === "CONSUMER"
      ? userData?.consumers?.find((ele) => ele?.consumerId === activeId)
      : userData?.generators?.find((ele) => ele?.generatorId === activeId);

  const capId = capacityId || location?.search?.split("=")[1] || id;

  // ── State ──────────────────────────────────────────────────────────────────
  const [file, setFile] = useState(null);
  const [fileUrl, setFileUrl] = useState(null);
  const [charges, setCharges] = useState([]);
  const [missingCharges, setMissingCharges] = useState([]);
  const [missingHeader, setMissingHeader] = useState(null);
  const [tempPath, setTempPath] = useState(null);

  const [chargeReqModal, setChargeReqModal] = useState(null);

  const [specialAccount, setSpecialAccount] = useState({});

  const [isGenerating, setIsGenerating] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState({
    status: false,
    data: "",
    note: "",
    header: "",
    handleSubmit: null,
    viewOk: false,
  });

  // ── Effects ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!file) {
      setFileUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setFileUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // ── Wallet helpers ─────────────────────────────────────────────────────────
  const mapCharges = useCallback(
    (res, role) => {
      const entityId =
        role === "CONSUMER"
          ? activeEntity?.consumerId
          : activeEntity?.generatorId;
      const entityLabel =
        role === "CONSUMER"
          ? activeEntity?.consumerName
          : activeEntity?.generatorName;
      const rawCharges = res?.data?.data?.walletAccounts?.[0]?.charges ?? [];
      return rawCharges
        .filter((ele) => ele?.frequency !== "RECURRING")
        .map((item) => ({
          ...item,
          entityName: entityLabel,
          sourceType: "WALLET",
          consumerId: role === "CONSUMER" ? entityId : null,
          generatorId: role === "GENERATOR" ? entityId : null,
          uniqueId: item.id || `wallet-${Math.random()}`,
          bearer: role === "CONSUMER" ? "BUYER" : "SELLER",
          payer: role === "CONSUMER" ? "BUYER" : "SELLER",
        }));
    },
    [activeEntity],
  );

  const fetchAllWalletCharges = useCallback(async () => {
    try {
      const [consumerRes, generatorRes] = await Promise.all([
        walletAccountApi({
          role: "CONSUMER",
          id: connection?.capacityConsumer?.consumerId,
        }),
        walletAccountApi({
          role: "GENERATOR",
          id: connection?.capacityGenerator?.generatorId,
        }),
      ]);

      const merged = [
        ...mapCharges(consumerRes, "CONSUMER"),
        ...mapCharges(generatorRes, "GENERATOR"),
      ];
      const seen = new Set();
      setCharges(
        merged.filter((c) => {
          if (seen.has(c.uniqueId)) return false;
          seen.add(c.uniqueId);
          return true;
        }),
      );
    } catch (e) {
      console.error("Error fetching wallet charges", e);
    }
  }, [connection, mapCharges]);

  const fetchSpecialAccounts = useCallback(async () => {
    try {
      const res = await getWalletAccountsSpecial("SUPER_ADMIN");
      if (res?.data?.code === 200) {
        const accounts = res?.data?.data?.[0] || {};

        setSpecialAccount(accounts);
      } else {
        console.error("Failed to fetch special accounts", res);
      }
    } catch (e) {
      console.error("Error fetching special accounts", e);
    }
  }, []);

  useEffect(() => {
    fetchAllWalletCharges();
    fetchSpecialAccounts();
  }, [fetchAllWalletCharges]);

  const getWalletId = async ({ role, id }) => {
    const res = await walletAccountApi({ role, id });
    return res?.data?.code === 200
      ? (res?.data?.data?.walletAccounts?.[0]?.id ?? null)
      : null;
  };

  // ── Error mail ─────────────────────────────────────────────────────────────
  const errorMailHandle = async (msg) => {
    const payload = {
      templateName: "DisasterManagementClientNotification",
      customData: { subject: "LDC Error", body: msg },
      toEmail: "stage-otc@gna.energy",
      attachmentS3Paths: [tempPath],
    };
    const res = await sendErrorMail(payload);
    if (res?.data?.code === 200) {
      window.location.reload();
    } else {
      console.error("Failed to send error mail", res);
    }
  };

  // ── PDF parsing ────────────────────────────────────────────────────────────
  const parseLDCPdf = async (file) => {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

    const pageTexts = await Promise.all(
      Array.from({ length: pdf.numPages }, async (_, i) => {
        const page = await pdf.getPage(i + 1);
        const content = await page.getTextContent();
        return content.items.map((item) => item.str).join(" ");
      }),
    );
    const fullText = pageTexts.join(" ");

    const scheduleMatch = fullText.match(
      /Payment Schedule[:\s]*([\s\S]*?)(?:Capacity approved|$)/i,
    );
    if (!scheduleMatch) return null;

    const rowRegex =
      /([A-Z][A-Z0-9_]+)\s+\d{2}-\d{2}-\d{4}\s+([\d.]+)\s+([\d.]+)\s+(\d+)\s+([\d.]+)/g;
    const payments = [];
    let m;
    while ((m = rowRegex.exec(scheduleMatch[1])) !== null) {
      payments.push({
        name: m[1],
        rate: parseFloat(m[2]),
        MWh: parseFloat(m[3]),
        days: parseInt(m[4], 10),
        totalAmount: parseFloat(m[5]),
      });
    }

    const totalPayable = parseFloat(
      (fullText.match(/Total Payable Amount\s+([\d.]+)/) || [])[1] ?? "0",
    );

    return {
      meta: { fileName: file.name, extractedAt: new Date().toISOString() },
      acceptanceNo:
        (fullText.match(/Acceptance No[:\s]+([^\s]+)/i) || [])[1] ?? null,
      totalPayableAmount: totalPayable,
      payments,
    };
  };

  // ── Charge update from PDF ─────────────────────────────────────────────────
  const updateChargesWithExtractedData = (payments) => {
    const chargeNames = new Set(charges.map((c) => c.name));
    const missing = payments.filter((p) => !chargeNames.has(p.name));

    if (missing.length > 0) {
      setMissingCharges(missing);
      setMissingHeader("Charges found in LDC but not in Wallet:");
      errorMailHandle(
        `Charges found in LDC but not in Wallet: ${missing.map((m) => m.name).join(", ")}. File: ${file?.name}`,
      );
    }

    setCharges((prevCharges) => {
      // Names that have a consumer (BUYER) charge — generator charges with the
      // same name are skipped; consumer has priority.
      const buyerChargeNames = new Set(
        prevCharges.filter((c) => c.bearer === "BUYER").map((c) => c.name),
      );

      return prevCharges.map((charge) => {
        const match = payments.find((p) => p.name === charge.name);
        if (!match) return charge;

        // Consumer charge — always assign
        if (charge.bearer === "BUYER") {
          return { ...charge, value: match.totalAmount || 0 };
        }

        // Generator charge — only assign if no consumer charge with this name
        if (charge.bearer === "SELLER" && !buyerChargeNames.has(charge.name)) {
          return { ...charge, value: match.totalAmount || 0 };
        }

        return charge;
      });
    });
  };

  // ── File handlers ──────────────────────────────────────────────────────────

  const handlePDFRandonAPI = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) {
      console.error("No file selected for upload.");
      return;
    }

    const selectedFile = files[0];
    const form = new FormData();
    form.append("file", selectedFile);
    const url = `${process.env.REACT_APP_API_URL}/doc/upload/public/random/AUTH`;

    try {
      const response = await axios.post(url, form, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `${token}`,
        },
      });
      if (response?.data?.code === 200) {
        setTempPath(response.data?.data?.path);
        console.error("Random API error:", response.data);
      }
    } catch (error) {
      console.error("Error calling Random API:", error);
    } finally {
    }
  };

  const handleFileUpload = async (e) => {
    const selected = e.target.files[0];
    if (!selected) return;

    if (selected.type !== "application/pdf") {
      ErrorMessage("Only PDF files are allowed!");
      await errorMailHandle(
        "Invalid file type uploaded. Only PDF files are allowed.",
      );
      return;
    }

    setFile(selected);

    handlePDFRandonAPI(e);

    try {
      const result = await parseLDCPdf(selected);

      if (!result) {
        const errMsg = "Payment Schedule section not found in the PDF.";
        ErrorMessage(errMsg);
        await errorMailHandle(
          `PDF parse error: ${errMsg} File: ${selected.name}`,
        );
        setMissingHeader(
          "Data not fetched from PDF. Please check the document format. Some assigned charges are currently blank/zero. You have the permissions to edit and allot the appropriate amounts,In case you want to add some new charges, click on Create Request button to send a request email to  admin for new charges creation.",
        );
        setConfirmOpen({
          viewOk: true,
          status: true,
          header: "PDF Data Not Fetched",
          data: "Data not fetched from PDF. Please check the document format. Some assigned charges are currently blank/zero. You have the permissions to edit and allot the appropriate amounts,In case you want to add some new charges, click on Create Request button to send a request email to  admin for new charges creation.",
          note: "",
          handleSubmit: () =>
            setConfirmOpen({
              status: false,
              data: "",
              note: "",
              header: "",
              handleSubmit: null,
              viewOk: false,
            }),
        });
        return;
      }

      setMissingHeader(null);
      setMissingCharges([]);

      if (result.payments.length === 0) {
        ErrorMessage(
          "No charge data found in the PDF. Please upload a valid LDC document.",
        );
      }

      updateChargesWithExtractedData(result.payments);
    } catch (error) {
      console.error("PDF Extraction Failed:", error);
      ErrorMessage(
        "Failed to parse the PDF. Please upload a valid LDC document.",
      );
      await errorMailHandle(
        `PDF Extraction Failed: ${error?.message || "Unknown error"}. File: ${selected.name}`,
      );
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const dropped = event.dataTransfer.files[0];
    if (dropped) setFile(dropped);
  };

  // ── Charge table handlers ──────────────────────────────────────────────────
  const handleChargeChange = (index, field, value) => {
    if (field === "value" && isNaN(value)) {
      ErrorMessage("Charge value must be a number");
      return;
    }
    setCharges((prev) =>
      prev.map((charge, i) =>
        i === index ? { ...charge, [field]: value } : charge,
      ),
    );
  };

  const handleBearerChange = (index, value) => {
    setCharges((prev) =>
      prev.map((charge, i) =>
        i === index ? { ...charge, bearer: value?.value ?? value } : charge,
      ),
    );
  };

  const handlePayerChange = (index, value) => {
    setCharges((prev) =>
      prev.map((charge, i) =>
        i === index ? { ...charge, payer: value?.value ?? value } : charge,
      ),
    );
  };

  // ── Submit ─────────────────────────────────────────────────────────────────
  const prepareSubmissionPayload = ({
    details,
    repSch,
    schId,
    tenderId,
    consumerWalletId,
    generatorWalletId,
  }) => {
    return details.map((ele) => ({
      id: null,
      fromUser:
        ele?.entityName ||
        (ele?.payer === "BUYER"
          ? connection?.capacityConsumer?.consumerName
          : connection?.capacityGenerator?.generatorName),
      fromUserId:
        ele?.bearer === "BUYER"
          ? userData?.activeConsumerId
          : userData?.activeGeneratorId,
      toUser: ele?.chargeType,
      toUserId: ele?.toEntityId,
      chargeName: ele?.name,
      chargeFrequency: ele?.frequency,
      chargeType: "FIXED",
      isAdminCharge: false,
      chargeAmount: ele?.value || 0,
      scheduleId: schId,
      chargeValue: ele?.value || 0,
      makerId: null,
      checkerId: null,
      actualChargeAmount: ele?.value,
      userInputChargeAmount: ele?.value || 0,
      makerInputChargeAmount: 0,
      checkerInputChargeAmount: 0,
      status: "MAKER",
      repScheduleId: repSch,
      generatorListingId: connection?.capacityGenerator?.name,
      consumerListingId: connection?.capacityConsumer?.name,
      consumerName: connection?.capacityConsumer?.consumerName,
      generatorName: connection?.capacityGenerator?.generatorName,
      bilateralContractId: connection?.contractUniqueId,
      comment: "",
      ...(tenderId ? { tenderId } : {}),
      bearer: ele?.bearer,
      payer: ele?.payer,
      debitWalletAccountId:
        ele?.bearer === "APPLICANT"
          ? specialAccount?.id || 0
          : ele?.bearer === "BUYER"
            ? consumerWalletId
            : generatorWalletId,
      creditWalletAccountId:
        ele?.payer === "APPLICANT"
          ? specialAccount?.id || 0
          : ele?.payer === "BUYER"
            ? consumerWalletId
            : generatorWalletId,
    }));
  };

  async function handleSubmit() {
    // const payload = prepareSubmissionPayload({
    //   details: charges,
    //   repSch: 1,
    //   schId: 2,
    //   tenderId:
    //     connection["CONSUMER"]?.priceDiscoveryType !== "BILATERAL"
    //       ? tenderData.id
    //       : null,
    //   consumerWalletId: 1,
    //   generatorWalletId: 2,
    // });
    setIsGenerating(true);
    try {
      const tranPayload = {
        contractId: connectionId,
        connectionId: connection?.bilateralConnections[0]?.id,
        consumerDetailId: connection?.capacityConsumer?.consumerId,
        generatorPlantId: connection?.capacityGenerator?.generatorId,
      };

      const transaction = await deliveryFinalSchedule(tranPayload);
      if (transaction?.data?.code !== 200) return;

      const repSch = transaction?.data?.data?.id;
      const schId = transaction?.data?.data?.scheduleId;
      const role =
        activeRole === "CONSUMER" ? "capacityConsumer" : "capacityGenerator";

      const [consumerWalletId, generatorWalletId] = await Promise.all([
        getWalletId({
          role: "CONSUMER",
          id: connection?.capacityConsumer?.consumerId,
        }),
        getWalletId({
          role: "GENERATOR",
          id: connection?.capacityGenerator?.generatorId,
        }),
      ]);

      const payload = prepareSubmissionPayload({
        details: charges,
        repSch,
        schId,
        tenderId:
          connection[role]?.priceDiscoveryType !== "BILATERAL"
            ? tenderData.id
            : null,
        consumerWalletId,
        generatorWalletId,
      });

      const settlementRes = await userFinalSettlement(payload);
      if (settlementRes?.data?.code !== 200) {
        ErrorMessage(
          settlementRes?.data?.error || "Failed to generate schedule.",
        );
        return;
      }

      try {
        const formData = new FormData();
        formData.append("file", file);
        const uploadRes = await uploadLDC(formData, connectionId);
        if (uploadRes?.data?.code === 200) {
          await handleCreateSchedule(connectionId);
        }
      } catch {
        ErrorMessage("Failed to upload LDC.");
      }

      dispatch(fetchActiveConnection({ curr: 0, capId: capId }));
      dispatch(fetchMainScreenActiveConnection({ capId: capId }));
      onClose();
    } catch (error) {
      ErrorMessage(
        error?.response?.data?.error || "An error occurred during submission.",
      );
    } finally {
      setIsGenerating(false);
    }
  }

  const handleAddchargeAndEmail = async (list) => {
    let res = await updateContractData(connection?.id, false);
    if (res?.data?.code === 200) {
      const newCharges = list.map((c) => ({
        ...c,
        uniqueId: `new-${Math.random()}`,
        sourceType: "REQUEST",
      }));
      const chargeItems = newCharges
        .map(
          (c) =>
            `>${c.name} — ₹${parseFloat(c.value).toLocaleString("en-IN", { minimumFractionDigits: 2 })} /n`,
        )
        .join("");
      const total = newCharges.reduce(
        (sum, c) => sum + (parseFloat(c.value) || 0),
        0,
      );

      errorMailHandle(
        `New Charge RequestFile: ${file?.name}
        Contract: ${connection?.contractUniqueId ?? connection?.id}
        Requested Charges:-${chargeItems}
        Total: ₹${total.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
      );

      setChargeReqModal(false);
      onClose();
    } else {
      ErrorMessage("Failed to add charge request. Please try again.");
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      {chargeReqModal && (
        <ChargeReqModal
          open={chargeReqModal}
          handleClose={() => setChargeReqModal(false)}
          handleSubmit={handleAddchargeAndEmail}
        />
      )}
      {confirmOpen?.status && (
        <ConfirmationModal
          open={confirmOpen.status}
          handleClose={() =>
            setConfirmOpen({
              status: false,
              data: "",
              note: "",
              header: "",
              handleSubmit: null,
              viewOk: false,
            })
          }
          data={confirmOpen.data}
          note={confirmOpen.note}
          header={confirmOpen.header}
          handleSubmit={confirmOpen.handleSubmit}
          viewOk={confirmOpen.viewOk}
          loading={isGenerating}
        />
      )}
      <Modal open={open} onClose={onClose}>
        <div className={`popupModal ${Styles.ldcMainContainer}`}>
          <div
            className={`display_flex_row modalHeading ${Styles.modalHeading}`}
          >
            <div className="headingText">Upload LDC Approval</div>
            <Close
              style={{ fontSize: 24, color: "#777777", cursor: "pointer" }}
              onClick={onClose}
            />
          </div>

          <div className={Styles.ldcModalContent}>
            {/* PDF Upload / Preview */}
            <div className={Styles.docUploadContainer}>
              {file ? (
                <div className={Styles.pdfPreviewContainer}>
                  <div className={Styles.pdfPreviewHeader}>
                    <span className={Styles.pdfFileName}>{file.name}</span>
                    <Close
                      style={{
                        fontSize: 18,
                        color: "#777777",
                        cursor: "pointer",
                      }}
                      onClick={() => setFile(null)}
                    />
                  </div>
                  <iframe
                    src={fileUrl ? `${fileUrl}#view=FitH` : ""}
                    title="PDF Preview"
                    className={Styles.pdfIframe}
                  />
                </div>
              ) : (
                <DndContext>
                  <div
                    ref={setNodeRef}
                    className={`${Styles.ldcDropzoneContainer} ${isOver ? Styles.dragOver : ""}`}
                    onClick={() => inputRef.current.click()}
                    onDrop={handleDrop}
                    onDragOver={(e) => e.preventDefault()}
                  >
                    <input
                      accept=".pdf"
                      type="file"
                      onChange={handleFileUpload}
                      ref={inputRef}
                      style={{ display: "none" }}
                    />
                    <div style={{ fontSize: "32px", color: "#bdbdbd" }}>+</div>
                    <div>Drag and drop,</div>
                    <div>
                      or click to{" "}
                      <span
                        style={{ color: "var(--primary-color-background)" }}
                      >
                        Upload
                      </span>{" "}
                      <b>LDC</b> Document
                    </div>
                  </div>
                </DndContext>
              )}
            </div>

            {/* Charges Table */}
            <div className={`${Styles.chargeMainContainer} scroll_bar_y`}>
              {!connection?.isChargesFinalized && (
                <div className={Styles.missingChargesWrapper}>
                  <div className={Styles.missingChargesHeader}>
                    Charge Addition requests is pending approval. Please wait
                    for the approval to generate schedule.
                  </div>
                </div>
              )}
              {missingHeader && (
                <div className={Styles.missingChargesWrapper}>
                  <div className={Styles.missingChargesContainer}>
                    <div className={Styles.missingChargesHeader}>
                      {missingHeader}
                    </div>
                    {missingCharges?.map((charge, idx) => (
                      <div key={idx} className={Styles.missingChargeItem}>
                        {charge.name} - ₹{charge.totalAmount}
                      </div>
                    ))}
                  </div>
                  <button
                    className={Styles.mailButton}
                    onClick={() => setChargeReqModal(true)}
                  >
                    <EmailOutlined style={{ fontSize: 14 }} />
                    Create Request
                  </button>
                </div>
              )}

              <div className="display_flex_row tableHeading">
                <div style={{ width: "30%" }}>Charge Name</div>
                <div style={{ width: "25%" }}>Charge Value</div>
                <div style={{ width: "15%" }}>Bearer</div>
                <div style={{ width: "15%" }}>Payer</div>
              </div>

              <div>
                {charges.map((ele, index) => (
                  <div
                    key={ele.uniqueId}
                    className="display_flex_row tableDetails"
                  >
                    <div style={{ width: "30%" }} className="textOverflow">
                      {ele.name}
                    </div>
                    <div
                      style={{ width: "25%" }}
                      className={Styles.chargeValueContainer}
                    >
                      <input
                        type="text"
                        value={ele.value}
                        onChange={(e) =>
                          handleChargeChange(index, "value", e.target.value)
                        }
                      />
                    </div>
                    <div style={{ width: "15%" }}>
                      {bearerList.find((b) => b.value === ele.bearer)?.value}
                      {/* <SelectInput
                        onChange={(v) => handleBearerChange(index, v)}
                        list={bearerList}
                        value={bearerList.find((b) => b.value === ele.bearer)}
                        example="Select Bearer"
                        height="35px"
                      /> */}
                    </div>
                    <div style={{ width: "15%" }}>
                      <SelectInput
                        onChange={(v) => handlePayerChange(index, v)}
                        list={payeeList}
                        value={payeeList.find((p) => p.value === ele.payer)}
                        example="Select Payer"
                        height="35px"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className={Styles.ldcModalFooter}>
            <div
              className={`activeButton_type_3 ${Styles.genScheduleButton}`}
              onClick={onClose}
            >
              Cancel
            </div>
            <div
              className={`${file && missingCharges?.length === 0 && connection?.isChargesFinalized && !isGenerating ? "activeButton" : "activeButton_disable"} ${Styles.genScheduleButton}`}
              onClick={() => {
                if (isGenerating) return;
                file &&
                  connection?.isChargesFinalized &&
                  missingCharges?.length === 0 &&
                  setConfirmOpen({
                    status: true,
                    data:
                      charges?.filter((ele) => ele?.value === 0)?.length > 0
                        ? missingHeader
                          ? "Failed to parse Charges"
                          : "Some charges still have 0 as values."
                        : missingCharges?.length > 0
                          ? "New charges detected"
                          : missingHeader
                            ? "Failed to parse Charges"
                            : "Confirm submission",
                    note:
                      charges?.filter((ele) => ele?.value === 0)?.length > 0
                        ? " Do you want to generate schedule?"
                        : missingCharges?.length > 0
                          ? "Do you want to add new charges?"
                          : missingHeader
                            ? "The assigned charges are currently blank/zero. You have the permissions to edit 	and allot the appropriate amounts."
                            : "Please confirm to generate the schedule based on the provided LDC and charges.",
                    header: "Generate Schedule",

                    handleSubmit: handleSubmit,
                    viewOk: false,
                  });
              }}
            >
              {isGenerating ? "Loading..." : "Generate Schedule"}
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default UploadLDC;
